// The Cloudflare Turnstile dance every site form shares (the landing form,
// web/site/form.ts; the login page, web/site/login.ts): on submit the fields
// fade out and the widget renders in their place — same box, nothing below
// moves — and its token is handed to `send`. A failed widget or a refused
// request brings the fields back with the sentence under them. The site key
// comes from the head's meta (config TURNSTILE_SITE_KEY, always set; the
// Cloudflare test key in dev renders a widget that passes by itself). The
// loader script is a page's own tag (index.html, login.html), not _head.html's:
// only the pages with a form carry it, and a page without it fails every
// submit with "still loading".

interface Turnstile {
  render(
    container: Element,
    options: {
      sitekey: string;
      theme?: "light" | "dark" | "auto";
      callback: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
    },
  ): string | undefined;
  remove(id: string | Element): void;
}

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

export interface FormParts {
  form: HTMLFormElement;
  /** The inputs and the button: hidden while the widget shows. */
  fields: HTMLElement;
  /** Where the widget renders. */
  slot: HTMLElement;
  /** The sentence under the form. */
  error: HTMLElement;
  button: HTMLButtonElement;
}

/** The parts a form needs, by the classes the pages share; null when the markup lacks one. */
export function formParts(form: HTMLFormElement): FormParts | null {
  const fields = form.querySelector<HTMLElement>(".maker-fields");
  const slot = form.querySelector<HTMLElement>(".maker-turnstile");
  const error = form.querySelector<HTMLElement>(".maker-error");
  const button = form.querySelector<HTMLButtonElement>("button[type=submit]");
  if (!fields || !slot || !error || !button) return null;
  return { form, fields, slot, error, button };
}

export function siteKey(): string {
  return (
    document.querySelector('meta[name="featurette-turnstile"]')?.getAttribute("content")?.trim() ??
    ""
  );
}

/**
 * Wires the form's submit: `validate` may refuse it before anything shows (a validity bubble);
 * then the widget, then `send(token)`. Returns `fail`, which `send` calls with the sentence to
 * show when the request was refused.
 */
export function turnstileSubmit(
  parts: FormParts,
  opts: { validate?: () => boolean; send: (token: string) => Promise<void> },
): { fail(text: string): void } {
  const { form, fields, slot, error, button } = parts;
  const sitekey = siteKey();
  let widget: string | undefined;

  const showFields = (on: boolean) => {
    fields.classList.toggle("away", !on);
    slot.hidden = on;
  };
  const dropWidget = () => {
    if (widget !== undefined) window.turnstile?.remove(widget);
    widget = undefined;
    slot.replaceChildren();
  };
  const fail = (text: string) => {
    dropWidget();
    showFields(true);
    error.textContent = text;
    error.hidden = false;
    button.disabled = false;
  };

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    if (opts.validate && !opts.validate()) return;
    const turnstile = window.turnstile;
    if (!turnstile) {
      fail("The spam check is still loading — try again in a second.");
      return;
    }
    error.hidden = true;
    button.disabled = true;
    showFields(false);
    widget = turnstile.render(slot, {
      sitekey,
      theme: "light",
      callback: (token) => void opts.send(token),
      "error-callback": () => fail("The spam check didn't load. Try again."),
      "expired-callback": () => fail("The spam check timed out. Try again."),
    });
    if (widget === undefined) fail("The spam check didn't load. Try again.");
  });
  return { fail };
}
